import { useEffect, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { AlertTriangle, Check, Circle } from "lucide-react";
import { seconds } from "../lib/format";
import { DUR, EASE_OUT } from "../lib/motion";

function StageIcon({ state }) {
  if (state === "done") return <Check size={12} aria-hidden="true" />;
  if (state === "failed") return <AlertTriangle size={12} aria-hidden="true" />;
  if (state === "active") return <span className="stage__spin" aria-hidden="true" />;
  return <Circle size={7} aria-hidden="true" />;
}

const STATE_WORDS = {
  pending: "not started",
  active: "in progress",
  done: "complete",
  failed: "failed",
};

/**
 * Live view of the analysis pipeline, driven by the backend's progress stream.
 *
 * Each row is a stage the server actually reported reaching, and the note on the
 * right is the detail it sent with it — which page, which document. The block is
 * a polite live region so a screen reader hears stage changes and the final
 * outcome without focus moving.
 */
function PipelineConsole({ stages, stageStates, stageDetails = {}, log, elapsed, status }) {
  const prefersReducedMotion = useReducedMotion();
  const termRef = useRef(null);

  // Keep the newest log line in view.
  useEffect(() => {
    const el = termRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log]);

  return (
    <div className="stack stack-4" aria-live="polite">
      <div className="stages">
        {stages.map((stage, index) => {
          const state = stageStates[index];
          const detail = stageDetails[stage.id];
          return (
            <div className="stage" key={stage.id} data-state={state}>
              <span className="stage__dot">
                <StageIcon state={state} />
              </span>
              <span className="stage__name">
                {stage.name}
                <span className="sr-only">, {STATE_WORDS[state]}</span>
              </span>
              <span className="stage__note">
                {state === "active" && detail ? detail : stage.note}
              </span>
            </div>
          );
        })}
      </div>

      <AnimatePresence initial={false}>
        {log.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: DUR.enter, ease: EASE_OUT }}
            style={{ overflow: "hidden" }}
          >
            <div className="term" ref={termRef}>
              {log.map((line) => (
                <div className="term__line" key={line.id}>
                  <span className="term__ts">{line.ts}</span>
                  <span
                    className={`term__msg${
                      line.tone === "ok" ? " term__msg--ok" : line.tone === "err" ? " term__msg--err" : ""
                    }`}
                  >
                    {line.msg}
                  </span>
                </div>
              ))}
              {(status === "uploading" || status === "working") && (
                <div className="term__line">
                  <span className="term__ts">{seconds(elapsed)}</span>
                  <span className="term__msg">
                    working
                    <span className="term__caret" aria-hidden="true" />
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default PipelineConsole;
